import {
  Text,
  Field,
  Item,
  getFieldValue,
  PlaceholderComponentProps,
  withPlaceholder,
  useSitecoreContext,
  LayoutServicePageState,
} from '@sitecore-jss/sitecore-jss-nextjs';
import withValidDatasourceCheck from 'lib/enhancers/withValidDatasourceCheck';
import classNames from 'classnames/bind';
import { useRouter } from 'next/router';
import { type ChangeEventHandler, useEffect, useState } from 'react';

import Select, { type SelectOption } from 'components/helpers/Select/Select';
import type { ComponentProps } from 'lib/component-props';
import compose from 'lib/enhancers/compose';
import withStyles from 'lib/enhancers/withStyles';
import type { RecordOfFields } from 'models/fields';

import styles from './DynamicDropdown.module.scss';
import type { DropdownOptionsProps } from './DropdownOption';
import { CustomInfo } from 'models/custom-resolver-types';
import { removeLineBreaks } from 'lib/utils';

type DynamicDropdownProps = ComponentProps &
  PlaceholderComponentProps & {
    fields: CustomInfo & {
      label: Field<string>;
      headingLevel: Item;
      textStyle: Item;
      description: Field<string>;
      defaultDisplayText: Field<string>;
    };
    placeholders: React.ReactElement<DropdownOptionsProps>[];
  };

const cx = classNames.bind(styles);

const DynamicDropdown = (props: DynamicDropdownProps): React.JSX.Element => {
  const { label, headingLevel, textStyle, description, defaultDisplayText, customInfo } = props.fields;
  const { className, placeholders } = props;
  const { sitecoreContext } = useSitecoreContext();
  const { pageEditing, pageState } = sitecoreContext;

  const datasourceName = customInfo?.datasourceItemName ?? '';
  const dropdownId = datasourceName === '' ? datasourceName : datasourceName.replaceAll(' ', '-');

  const router = useRouter();
  const [baseUrl, setBaseUrl] = useState<string>('');
  const [queryString, setQueryString] = useState<string>('');
  const [selected, setSelected] = useState<string>('');

  const headingLvl = headingLevel?.fields['Value'] as Field<string>;
  const textStyleItemField = textStyle?.fields as RecordOfFields;
  const txtStyleValue = getFieldValue<string>(textStyleItemField, 'Value') || 'font-style-xl';

  useEffect(() => {
    if (router.isReady) {
      if (router.asPath.indexOf('?') > 0) {
        // get corresponding value from query string id (dropdown datasource id)
        const matchingKeyValue = router.query[dropdownId];
        const dropdownQuery = (
          Array.isArray(matchingKeyValue) ? matchingKeyValue[0] : !matchingKeyValue ? '' : matchingKeyValue
        ).replaceAll(' ', '-');

        const initialSelected = dropdownQuery;
        const [initialBaseUrl, initialQueryString] = router.asPath.split('?');
        const splitQuery = initialQueryString.split('#');

        if (splitQuery.length > 1) {
          const observer = new MutationObserver(() => {
            const anchorTag = document.getElementById(splitQuery[1]);

            if (anchorTag) {
              anchorTag.scrollIntoView({ behavior: 'smooth', block: 'start' });
              observer.disconnect();
            }
          }); //if an anchor tag exists after the query tags, wait for it to appear then scroll to it

          observer.observe(document.body, { childList: true, subtree: true });
        }

        setBaseUrl(initialBaseUrl);
        setQueryString(splitQuery[0]);

        if (selected !== initialSelected) {
          setSelected(initialSelected);
        }
      } else if (router.asPath !== baseUrl) {
        setBaseUrl(router.asPath.split('#')[0]);
      }
    }
  }, [
    baseUrl,
    dropdownId,
    queryString,
    router.asPath,
    router.isReady,
    router.query,
    selected,
    setBaseUrl,
    setQueryString,
    setSelected,
  ]);

  const pickType: ChangeEventHandler<HTMLSelectElement> = (e) => {
    const { value } = e.target;

    let updated = false;
    const paramArray: [string, string][] = [];

    if (queryString !== '') {
      const initialSplit = queryString.split('&');

      initialSplit.map((x) => {
        const tempSplit = x.split('=');

        if (tempSplit[0] === encodeURI(dropdownId)) {
          paramArray.push([tempSplit[0], encodeURI(value)]);
          updated = true;
        } //Update existing value
        else {
          paramArray.push([tempSplit[0], tempSplit[1]]);
        }
      });
    } //if a query string exists, deconstruct and update it if possible

    if (!updated) {
      paramArray.push([encodeURI(dropdownId), encodeURI(value)]);
    } // if no value was updated, add it as a new item

    setSelected(value);

    const updateQuerystring = paramArray.map((element) => element.join('=')).join('&');
    setQueryString(updateQuerystring);

    if (updateQuerystring !== '' && pageState != LayoutServicePageState.Preview) {
      const newUrl = baseUrl + '?' + updateQuerystring;
      router.replace(newUrl, newUrl, { scroll: false });
    }
  };

  const classes = cx(className);

  if (pageEditing) {
    return (
      <div className={classes}>
        <Text
          field={label}
          tag={headingLvl?.value ?? 'div'}
          className={cx({ title: true, [txtStyleValue]: true })}
          encode={sitecoreContext.pageEditing}
        />
        <Select
          className={cx('dropdown')}
          checkSelectedOption={true}
          options={[{ value: defaultDisplayText.value }]}
          label={removeLineBreaks(description.value)}
          id={dropdownId}
          onChange={pickType}
          value={selected}
        />
        {placeholders}
      </div>
    );
  }

  const options: SelectOption[] = [];
  let displayedPanel: React.JSX.Element = <></>;

  placeholders.forEach((placeholder) => {
    const { fields } = placeholder?.props;

    const datasourceName = fields?.customInfo?.datasourceItemName ?? '';

    const option: SelectOption = {
      label: fields?.label,
      value: datasourceName === '' ? datasourceName : datasourceName.replaceAll(' ', '-'),
    };

    if (selected === option?.value) {
      displayedPanel = placeholder;
    }

    options.push(option);
  });

  return (
    <div className={classes}>
      <Text
        field={label}
        tag={headingLvl?.value ?? 'div'}
        className={cx({ title: true, [txtStyleValue]: true })}
        encode={sitecoreContext.pageEditing}
      />
      <Select
        className={cx('dropdown')}
        checkSelectedOption={true}
        options={options}
        label={removeLineBreaks(description.value)}
        id={dropdownId}
        onChange={pickType}
        placeholder={defaultDisplayText.value}
        value={selected}
      />
      <div className={cx('tabPanel')}>{displayedPanel}</div>
    </div>
  );
};

export default compose<DynamicDropdownProps>(
  withValidDatasourceCheck(),
  withPlaceholder({
    placeholder: 'hp-option',
    prop: 'placeholders',
  }),
  withStyles()
)(DynamicDropdown);
